import React, { useState } from 'react';
import { 
  MessageCircle, 
  FileText, 
  Shield, 
  ExternalLink,
  Loader2,
  ChevronDown,
  Send,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Plus,
  Crown,
  Users
} from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { useAuth } from '../../contexts/AuthContext';
import { createSupportInquiry, SupportInquiry } from '../../services/supportService';
import { updateAllUsersToProfessional } from '../../services/bulkSubscriptionUpdateService';
import { useNavigate } from 'react-router-dom';
import { SectionHeader } from '../../components/ui';

const Support: React.FC = () => {
  const { strings, language, isRTL } = useLocalization();
  const { presetData, getContactUsTypeName } = useSystemPreset();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Contact form states
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Admin bulk update states (only for specific admin user)
  const ADMIN_USER_ID = 'hcqBWpoKxXfMs9Nm7u2PQMfJqUY2';
  const isAdmin = user?.id === ADMIN_USER_ID;
  const [isUpdatingSubscriptions, setIsUpdatingSubscriptions] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string>(() => {
    // Default to January 15, 2026
    const defaultDate = new Date('2026-01-15');
    return defaultDate.toISOString().split('T')[0];
  });
  const [updateProgress, setUpdateProgress] = useState<{
    current: number;
    total: number;
    currentUserId: string;
    status: string;
  } | null>(null);
  const [updateResult, setUpdateResult] = useState<{
    success: boolean;
    message: string;
    successCount: number;
    failureCount: number;
    failures: Array<{ userId: string; reason: string }>;
  } | null>(null);

  // Default inquiry types if not available in SystemPresetData
  const defaultInquiryTypes = [
    { id: '1', name_en: 'General Inquiry', name_ar: 'استفسار عام' },
    { id: '2', name_en: 'Technical Support', name_ar: 'دعم فني' },
    { id: '3', name_en: 'Feature Request', name_ar: 'طلب ميزة' },
    { id: '4', name_en: 'Bug Report', name_ar: 'تقرير خطأ' },
    { id: '5', name_en: 'Account Issue', name_ar: 'مشكلة في الحساب' },
    { id: '6', name_en: 'Billing Question', name_ar: 'سؤال حول الفواتير' }
  ];

  const inquiryTypes = (presetData?.contactUsTypes && presetData.contactUsTypes.length > 0)
    ? presetData.contactUsTypes 
    : defaultInquiryTypes;

  // Debug logs for ContactUsTypes
  console.log('🔍 Debug - Support: ContactUsTypes debug', {
    presetDataExists: !!presetData,
    contactUsTypesExists: !!presetData?.contactUsTypes,
    contactUsTypesLength: presetData?.contactUsTypes?.length || 0,
    usingDefaultTypes: !presetData?.contactUsTypes || presetData.contactUsTypes.length === 0,
    inquiryTypesLength: inquiryTypes.length,
    inquiryTypes: inquiryTypes.map(type => ({ id: type.id, name_en: type.name_en, name_ar: type.name_ar }))
  });

  const handleTermsClick = () => {
    navigate('/terms-and-conditions');
  };

  const handlePrivacyClick = () => {
    navigate('/privacy-policy');
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = '+201202322241';
    const message = 'Hello! I need support with the Morafaa System.';
    const whatsappUrl = `https://wa.me/${phoneNumber.replace('+', '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSubmitInquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔍 Debug - Support: Form submission started', {
      selectedType,
      message: message.trim(),
      user: user ? { 
        id: user.id, 
        email: user.email, 
        firstName: user.firstName,
        middleName: user.middleName,
        lastName: user.lastName,
        constructedName: [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ')
      } : null
    });
    
    if (!selectedType || !message.trim()) {
      console.log('⚠️ Debug - Support: Form validation failed', {
        selectedType,
        messageLength: message.trim().length
      });
      return;
    }

    if (!user) {
      console.log('⚠️ Debug - Support: No user found');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Construct user name from firstName, middleName, lastName
      const userName = [user.firstName, user.middleName, user.lastName]
        .filter(Boolean)
        .join(' ') || 'Unknown User';

      const inquiryData: Omit<SupportInquiry, 'id' | 'createdAt' | 'updatedAt'> = {
        type: selectedType, // This is the typeId from ContactUsTypes
        message: message.trim(),
        status: 0, // pending
        userId: user.id,
        userEmail: user.email || '',
        userName: userName
      };

      console.log('🔍 Debug - Support: Submitting inquiry data:', inquiryData);

      await createSupportInquiry(inquiryData);
      
      console.log('✅ Debug - Support: Inquiry submitted successfully');
      setSubmitStatus('success');
      setSelectedType('');
      setMessage('');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
      
    } catch (error) {
      console.error('❌ Debug - Support: Error submitting inquiry:', error);
      console.error('❌ Debug - Support: Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        code: (error as any)?.code || 'No code',
        stack: error instanceof Error ? error.stack : 'No stack'
      });
      setSubmitStatus('error');
      
      // Reset error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = () => {
    // TODO: Add refresh functionality
    console.log('Refresh support clicked');
  };

  const handleAddTicket = () => {
    // TODO: Add ticket functionality
    console.log('Add ticket clicked');
  };

  // Admin function: Update all users to Professional Plan
  const handleBulkUpdateSubscriptions = async () => {
    if (!isAdmin) {
      console.error('❌ Unauthorized: Only admin user can perform this action');
      return;
    }

    // Validate date
    if (!subscriptionEndDate) {
      alert(language === 'ar' 
        ? '⚠️ يرجى اختيار تاريخ انتهاء الاشتراك'
        : '⚠️ Please select an end date');
      return;
    }

    // Parse selected date and set to end of day (11:59:59 PM)
    const selectedDate = new Date(subscriptionEndDate);
    if (isNaN(selectedDate.getTime())) {
      alert(language === 'ar' 
        ? '⚠️ تاريخ غير صحيح'
        : '⚠️ Invalid date');
      return;
    }

    // Set to end of selected day (23:59:59.999)
    selectedDate.setHours(23, 59, 59, 999);
    const endDateTimestamp = selectedDate.getTime().toString();
    const formattedDate = selectedDate.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Confirm action
    const confirmMessage = language === 'ar'
      ? `⚠️ هل أنت متأكد؟ سيتم تحديث جميع المستخدمين إلى الخطة المهنية حتى ${formattedDate}`
      : `⚠️ Are you sure? This will update ALL users to Professional Plan until ${formattedDate}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsUpdatingSubscriptions(true);
    setUpdateProgress(null);
    setUpdateResult(null);

    try {
      // Use the selected end date
      const endDate = endDateTimestamp;

      const result = await updateAllUsersToProfessional(
        endDate,
        (current, total, userId, status) => {
          setUpdateProgress({ current, total, currentUserId: userId, status });
        }
      );

      setUpdateResult(result);
      console.log('✅ Bulk update completed:', result);
    } catch (error: any) {
      console.error('❌ Error in bulk update:', error);
      setUpdateResult({
        success: false,
        message: error.message || 'Unknown error occurred',
        successCount: 0,
        failureCount: 0,
        failures: []
      });
    } finally {
      setIsUpdatingSubscriptions(false);
      setUpdateProgress(null);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <SectionHeader
          title={strings.support.title}
          description={strings.support.aboutDescription}
          language={language}
          isRTL={isRTL}
          refreshButton={{
            onClick: handleRefresh,
            disabled: false,
            loading: false
          }}
          addButton={{
            label: language === 'ar' ? 'إضافة تذكرة' : 'Add Ticket',
            onClick: handleAddTicket
          }}
        />

        {/* Contact Form - Based on Mobile App Design */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
            <MessageCircle className="w-6 h-6 mr-3 text-primary" />
            {strings.support.contactUs}
          </h2>

          <form onSubmit={handleSubmitInquiry} className="space-y-6">
            {/* Type Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {strings.support.inquiryType}
              </label>
              <button
                type="button"
                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                className="w-full p-4 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-left flex items-center justify-between hover:border-primary transition-colors"
              >
                <span className={selectedType ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                  {selectedType 
                    ? (() => {
                        const typeName = getContactUsTypeName(selectedType, language);
                        console.log('🔍 Debug - Support: Displaying selected type', {
                          selectedType,
                          language,
                          typeName,
                          allTypes: inquiryTypes.map(t => ({ id: t.id, name_en: t.name_en, name_ar: t.name_ar }))
                        });
                        return typeName;
                      })()
                    : strings.support.selectType
                  }
                </span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showTypeDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {inquiryTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => {
                        console.log('🔍 Debug - Support: Type selected', {
                          typeId: type.id,
                          typeName_en: type.name_en,
                          typeName_ar: type.name_ar,
                          currentLanguage: language
                        });
                        setSelectedType(type.id);
                        setShowTypeDropdown(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-600 first:rounded-t-xl last:rounded-b-xl"
                    >
                      <span className="text-gray-900 dark:text-white">
                        {language === 'ar' ? type.name_ar : type.name_en}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Message Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {strings.support.message}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={strings.support.messagePlaceholder}
                rows={6}
                className="w-full p-4 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl resize-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!selectedType || !message.trim() || isSubmitting}
              className="w-full bg-primary text-white py-4 px-6 rounded-xl font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {strings.support.sending}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {strings.support.send}
                </>
              )}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
                <div>
                  <p className="text-green-800 dark:text-green-200 font-medium">
                    {strings.support.inquirySent}
                  </p>
                  <p className="text-green-600 dark:text-green-400 text-sm">
                    {strings.support.inquirySentMessage}
                  </p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" />
                <div>
                  <p className="text-red-800 dark:text-red-200 font-medium">
                    {strings.support.errorSending}
                  </p>
                  <p className="text-red-600 dark:text-red-400 text-sm">
                    {strings.support.errorSendingMessage}
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Support Options Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Social Media Links */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{strings.support.connectWithUs}</h3>
            <div className="space-y-4">
              <a
                href="https://www.facebook.com/morafaaapp"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 bg-primary text-white rounded-xl hover:bg-blue-700 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="w-6 h-6 mr-3" />
                <span className="font-semibold">{strings.support.facebook}</span>
                <ExternalLink className="w-4 h-4 ml-auto" />
              </a>
              
              <a
                href="https://www.instagram.com/morafaaapp/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="w-6 h-6 mr-3" />
                <span className="font-semibold">{strings.support.instagram}</span>
                <ExternalLink className="w-4 h-4 ml-auto" />
              </a>
            </div>
          </div>

          {/* Customer Support */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{strings.support.customerSupport}</h3>
            <div className="space-y-4">
              <button
                onClick={handleWhatsAppClick}
                className="w-full flex items-center p-4 bg-[#25D366] text-white rounded-xl hover:bg-[#128C7E] transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <MessageCircle className="w-6 h-6 mr-3" />
                <div className="text-left">
                  <div className="font-semibold">{strings.support.whatsappSupport}</div>
                  <div className="text-sm opacity-90">+201202322241</div>
                </div>
                <ExternalLink className="w-4 h-4 ml-auto" />
              </button>
            </div>
          </div>
        </div>

        {/* Admin Bulk Update Section (Temporary - Only for specific admin user) */}
        {isAdmin && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-2xl shadow-lg p-6 mb-8">
            <div className="flex items-center mb-4">
              <Crown className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mr-3" />
              <h3 className="text-xl font-bold text-yellow-900 dark:text-yellow-200">
                {language === 'ar' ? '🔧 أدوات الإدارة (مؤقتة)' : '🔧 Admin Tools (Temporary)'}
              </h3>
            </div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-4">
              {language === 'ar' 
                ? '⚠️ هذا القسم مؤقت وسيتم إزالته بعد التأكد من صحة البيانات'
                : '⚠️ This section is temporary and will be removed after verifying data integrity'}
            </p>
            
            <div className="space-y-4">
              {/* Date Selector */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-yellow-300 dark:border-yellow-700">
                <label className="block text-sm font-medium text-yellow-900 dark:text-yellow-200 mb-2">
                  {language === 'ar' ? 'تاريخ انتهاء الاشتراك' : 'Subscription End Date'}
                </label>
                <input
                  type="date"
                  value={subscriptionEndDate}
                  onChange={(e) => setSubscriptionEndDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  disabled={isUpdatingSubscriptions}
                  className="w-full p-3 bg-white dark:bg-gray-700 border border-yellow-400 dark:border-yellow-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                  {language === 'ar' 
                    ? 'سيتم تعيين وقت انتهاء الاشتراك إلى 11:59:59 مساءً في اليوم المحدد'
                    : 'Subscription will expire at 11:59:59 PM on the selected date'}
                </p>
              </div>

              {/* Bulk Update Button */}
              <button
                onClick={handleBulkUpdateSubscriptions}
                disabled={isUpdatingSubscriptions || !subscriptionEndDate}
                className="w-full flex items-center justify-center p-4 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {isUpdatingSubscriptions ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                    <span>{language === 'ar' ? 'جاري التحديث...' : 'Updating...'}</span>
                  </>
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-3" />
                    <span>
                      {language === 'ar' 
                        ? 'تحديث جميع المستخدمين إلى الخطة المهنية'
                        : 'Update All Users to Professional Plan'}
                    </span>
                  </>
                )}
              </button>

              {/* Progress Display */}
              {updateProgress && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-yellow-300 dark:border-yellow-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-yellow-900 dark:text-yellow-200">
                      {language === 'ar' ? 'التقدم' : 'Progress'}
                    </span>
                    <span className="text-sm text-yellow-700 dark:text-yellow-300">
                      {updateProgress.current} / {updateProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-yellow-200 dark:bg-yellow-800 rounded-full h-2">
                    <div 
                      className="bg-yellow-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(updateProgress.current / updateProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                    {language === 'ar' ? 'المستخدم الحالي:' : 'Current user:'} {updateProgress.currentUserId}
                    {' - '}
                    {updateProgress.status === 'processing' ? (language === 'ar' ? 'جاري المعالجة' : 'Processing') :
                     updateProgress.status === 'success' ? (language === 'ar' ? 'نجح' : 'Success') :
                     updateProgress.status === 'failed' ? (language === 'ar' ? 'فشل' : 'Failed') : ''}
                  </p>
                </div>
              )}

              {/* Result Display */}
              {updateResult && (
                <div className={`rounded-xl p-4 border-2 ${
                  updateResult.success 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600' 
                    : 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600'
                }`}>
                  <div className="flex items-start">
                    {updateResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-3 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-semibold mb-2 ${
                        updateResult.success 
                          ? 'text-green-900 dark:text-green-200' 
                          : 'text-red-900 dark:text-red-200'
                      }`}>
                        {updateResult.success 
                          ? (language === 'ar' ? '✅ اكتمل التحديث' : '✅ Update Completed')
                          : (language === 'ar' ? '❌ فشل التحديث' : '❌ Update Failed')
                        }
                      </p>
                      <p className={`text-sm mb-2 ${
                        updateResult.success 
                          ? 'text-green-700 dark:text-green-300' 
                          : 'text-red-700 dark:text-red-300'
                      }`}>
                        {updateResult.message}
                      </p>
                      <div className={`text-sm ${
                        updateResult.success 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        <p>
                          {language === 'ar' ? 'نجح:' : 'Success:'} {updateResult.successCount} | {' '}
                          {language === 'ar' ? 'فشل:' : 'Failed:'} {updateResult.failureCount}
                        </p>
                      </div>
                      {updateResult.failures.length > 0 && (
                        <div className="mt-2 max-h-32 overflow-y-auto">
                          <p className="text-xs font-medium text-red-800 dark:text-red-300 mb-1">
                            {language === 'ar' ? 'المستخدمون الفاشلون:' : 'Failed users:'}
                          </p>
                          {updateResult.failures.slice(0, 5).map((failure, idx) => (
                            <p key={idx} className="text-xs text-red-700 dark:text-red-400">
                              - {failure.userId}: {failure.reason}
                            </p>
                          ))}
                          {updateResult.failures.length > 5 && (
                            <p className="text-xs text-red-600 dark:text-red-500">
                              ... and {updateResult.failures.length - 5} more
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Legal Documents */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{strings.support.legalInformation}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={handleTermsClick}
              className="flex items-center p-4 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <FileText className="w-6 h-6 mr-3 text-blue-600" />
              <div className="text-left">
                <div className="font-semibold">{strings.support.termsConditions}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{strings.support.viewTerms}</div>
              </div>
            </button>
            
            <button
              onClick={handlePrivacyClick}
              className="flex items-center p-4 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Shield className="w-6 h-6 mr-3 text-green-600" />
              <div className="text-left">
                <div className="font-semibold">{strings.support.privacyPolicy}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{strings.support.viewPrivacy}</div>
              </div>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Support;